import React from 'react';
import { required, minValue, maxValue, List, SaveButton, Datagrid, DateField, TextField, NumberField, Create, Edit, FormTab, TabbedForm, Toolbar, BooleanInput, TextInput, EditButton, Filter, NumberInput, Labeled } from 'react-admin';
import Box from '@material-ui/core/Box';

import { isAdminRole, SectionHeader, TimezoneSelectInput, twoColumnGrid, gridFullWidth } from './components/utils';

const AdvertiserTitle = ({ record }) => {
    return <span>Advertiser {record ? `"${record.name}"` : ''}</span>;
};

const AdvertiserFilter = (props) => (
    <Filter {...props}>
        <TextInput label="Search" source="search_name" alwaysOn resettable />
    </Filter>
);
export const AdvertiserList = (props) => (
    <List {...props} filters={<AdvertiserFilter />} perPage={25} bulkActionButtons={false}>
        <Datagrid rowClick="edit">
            <TextField source="code" label="ID" />
            <TextField source="name" label="Advertiser/Company Name" />
            <TextField source="website" label="Website" />
            <DateField source="updated_at" label="Update Date" />
			<DateField source="created_at" label="Creation Date" />
            <EditButton variant="contained" color="primary" />
        </Datagrid>
    </List>
);
// Grid cell width. Inputs inside a grid Box get no automatic width from
// react-admin (no FormInput wrapper), so each one is told to fill its column.
const cell = { width: '100%' };

/**
 * The advertiser form body, shared by Edit and Create.
 *
 * Previously duplicated field-for-field between the two, which is how the
 * limit fields had to be added twice and how the two would eventually drift.
 * Safe as a wrapper component here (unlike a FormTab wrapper - see
 * advertiser/campaigns.js): inputs bind through form context, and read-only
 * Fields resolve the record through react-admin 3.19's RecordContext, so
 * neither depends on the props FormTab injects into its literal children.
 *
 * `isEdit` gates the Advertiser ID, which does not exist until after save.
 */
const AdvertiserFormFields = ({ permissions, isEdit }) => (
    <Box style={{ ...twoColumnGrid, width: '75%', maxWidth: 1000 }}>
        <SectionHeader title="Advertiser" />
        {isEdit && (
        <Labeled label="Advertiser ID" style={gridFullWidth}>
            <NumberField source="code" style={{ fontSize: '1rem' }} />
        </Labeled>
        )}
        <TextInput source="name" label="Advertiser/Company Name" validate={required()} style={cell} />
        <TextInput source="website" label="Website" validate={required()} style={cell} />
        <TextInput source="tldomain" label="Top Level Domain" validate={required()} style={cell} />

        {isAdminRole(permissions) && <SectionHeader title="Pricing" />}
        {isAdminRole(permissions) &&
          <NumberInput source="platform_margin_pct" label="Platform Margin (%)"
            step={1} min={0} max={99} style={cell}
            validate={[minValue(0), maxValue(99)]}
            helperText="The platform's take on this advertiser's campaigns: publisher payout = gross bid x (1 - margin). 0 or empty = no margin." />}
        {isAdminRole(permissions) &&
          <BooleanInput source="dynamic_pricing_enabled" label="Enable Dynamic Pricing" defaultValue={false}
            helperText="Shows the Dynamic Pricing toggle on this advertiser's campaigns." />}

        {/* Spend limits bound EVERY campaign under this advertiser, however it
            was created - ad ops, the console, or /api/v1. They are not tied to
            self-serve access, which is granted per user. All three are enforced
            server-side; the console only configures them (NBU-5).

            Both values are REQUIRED: there is no uncapped advertiser. The
            validators here are a courtesy that fails fast in the form - the
            binding check is missingRequiredLimit in the insights service layer,
            which also covers /api/v1 and any caller for whom these inputs never
            render (they are admin-only, and a validator on an unrendered input
            never runs). */}
        {isAdminRole(permissions) && <SectionHeader title="Spend Limits" />}
        {isAdminRole(permissions) &&
          <NumberInput source="monthly_spend_limit" label="Monthly Spend Limit (USD)"
            step={0.01} min={0} style={cell}
            validate={[required(), minValue(0.01)]}
            helperText="Required. Campaigns pause automatically once this advertiser's spend for the month reaches this limit." />}
        {isAdminRole(permissions) &&
          <NumberInput source="max_bid_price" label="Max Bid Price (USD CPM)"
            step={0.01} min={0} style={cell}
            validate={[required(), minValue(0.01)]}
            helperText="Required. Maximum CPM this advertiser's campaigns may bid. Bounds burn rate while a spend-limit pause takes effect (up to ~10 minutes)." />}
        {/* Was a free-text input. AdvertiserLimitCoordinator resolves this with
            Intl and falls back silently on an unknown name, so a typo moved an
            advertiser's BILLING month boundary with no error - the same hazard
            as the campaign flight timezone, on a field that decides when spend
            resets to zero.

            Keeps its historical UTC default rather than taking the shared
            Eastern one: this is a billing boundary, and changing when the month
            rolls over for newly created advertisers is not a side effect a
            layout change should have. */}
        {isAdminRole(permissions) &&
          <TimezoneSelectInput source="advertiser_limit_timezone" label="Monthly Reset Timezone"
            defaultValue="UTC" style={cell}
            helperText="The month boundary that resets this advertiser's spend total. The Mongo field is advertiser_limit_timezone." />}
    </Box>
);

export const AdvertiserEdit = ({ permissions, ...props }) => {
    return (<Edit title={<AdvertiserTitle />} {...props}>
        <TabbedForm toolbar={<CustomToolbar />}>
            <FormTab label="Advertiser Settings">
                <AdvertiserFormFields permissions={permissions} isEdit />
            </FormTab>
        </TabbedForm>
    </Edit>
    )
}
export const AdvertiserCreate = ({ permissions, ...props }) => {
    return (<Create title={<AdvertiserTitle />} {...props}>
        <TabbedForm toolbar={<CustomToolbar />}>
            <FormTab label="Advertiser Settings">
                <AdvertiserFormFields permissions={permissions} />
            </FormTab>
        </TabbedForm>
    </Create>
    )
}
const CustomToolbar = props => (
    <Toolbar {...props}>
        <SaveButton />
        <br />
        <DateField source="updated_at" label="Update date" style={{ padding: '10px' }} showTime />
        <TextField source="updated_name" label="" style={{ padding: '10px' }} />
    </Toolbar>
);
